import React, { useCallback, useEffect, useState } from 'react';

import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { Loader } from '@cube3/cubicle/src/core/atoms/Loader/Loader';
import { getThemeColor, Icon } from '@cube3/cubicle/src/core/atoms/Icon';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import MaybeTooltip from '@cube3/cubicle/src/core/atoms/Tooltip/MaybeTooltip';
import { makeCSS } from '@cube3/cubicle/src/utils/makeCSS';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Tooltip } from '@cube3/cubicle/src/core/atoms/Tooltip/Tooltip';
import { isMobile } from 'react-device-detect';
import { Scrollbar } from '@cube3/cubicle/src/core/atoms/Scrollbar';

export type ThumbnailType = {
  thumbnail: string;
  captured_at: number;
};

export interface SnapshotType extends ThumbnailType {
  id: string;
  processing: boolean;
  failed: boolean;
  display_image?: string;
}

interface SnapshotsListProps {
  snapshots: SnapshotType[];
  /** Whether can take snapshot */
  canCapture: boolean;
  loading: boolean;
  error: string;
  onClickSnapshot: () => void;
  /** Set the current frame as thumbnail */
  saveThumbnail: () => void;
  saveThumbnailStatus: 'failed' | 'loading' | undefined;
  onClickThumbnail: (playedSeconds: number) => void;
  playedSeconds: number;
  showSnapshotsList: boolean;
}

const useStyles = makeCSS(({ theme }) => ({
  root: {
    backgroundColor: theme.color.black['80'], //rgba(0, 0, 0, 0.8),
    height: 160,
    boxSizing: 'border-box'
  },
  mobileRoot: {
    backgroundColor: theme.color.black['80'],
    boxSizing: 'border-box',
    height: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  mobile: {},
  helperText: {
    paddingLeft: 24,
    paddingTop: isMobile ? 12 : ''
  },
  listItem: {
    position: 'relative',
    animation: '$fade 1s linear'
  },
  listItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    position: 'relative',
    maxHeight: isMobile ? 200 : 160,
    flexWrap: 'wrap'
  },
  imageContainer: {
    borderRadius: '5px',
    border: `solid 1px ${theme.color.white['24']}`, //rgba(255, 255, 255, 0.24)
    maxWidth: 160,
    maxHeight: 90,
    overflow: 'hidden'
  },
  image: {
    borderRadius: 'inherit',
    maxWidth: 'inherit',
    maxHeight: 'inherit',
    objectFit: 'contain',
    objectPosition: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.7
    }
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    height: 'auto',
    width: 'auto',
    background: theme.color.black['48'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    animation: '$fade 1s linear'
  },
  snapshotsList: {
    display: 'flex',
    flexDirection: isMobile ? 'column-reverse' : 'row-reverse',
    alignItems: isMobile ? 'flex-start' : 'center',
    bottom: '0',
    padding: '24px 24px 20px 24px',
    gap: 16,
    listStyle: 'none',
    justifyContent: 'start',
    flexWrap: 'nowrap',
    height: isMobile ? 200 : 90,
    top: 'calc(100% - 90px)',
    left: 0,
    margin: 0,
    overflow: isMobile ? '' : 'auto hidden',
    '&$mobile': {
      padding: theme.spacing['02'], // 8px
      position: 'absolute',
      width: 'calc(100% - 16px)',
      bottom: 0
    }
  },
  snapshotButton: {
    cursor: 'pointer',
    display: 'inline-flex',
    borderRadius: '6px',
    border: `dashed 1px ${theme.color.background.white}`,
    alignItems: 'center',
    textAlign: 'center',
    boxSizing: 'border-box',
    flexDirection: 'column',
    padding: '12px',
    width: 160,
    height: 90,
    '&:hover': {
      backgroundColor: theme.color.overlay.hovered
    }
  },
  secondaryBorder: { border: `dashed 1px ${theme.color.background.secondary}` },
  yellowBorder: { border: `dashed 1px ${theme.color.background.yellow.main}` },
  disabled: {
    opacity: 0.5,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'inherit'
    }
  },
  relative: {
    position: 'relative',
    opacity: 1
  },

  '@keyframes fade': {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  }
}));

export const SnapshotsList: React.FunctionComponent<SnapshotsListProps> = (
  props
) => {
  const {
    onClickSnapshot,
    saveThumbnail,
    onClickThumbnail,
    saveThumbnailStatus,
    canCapture = false,
    snapshots = [],
    error = undefined,
    loading,
    playedSeconds,
    showSnapshotsList
  } = props;

  const classes = useStyles();

  const [clicked, setClicked] = useState(false);
  const canSaveThumnail = !loading && !clicked;

  const onSaveThumbnail = useCallback(() => {
    saveThumbnail();
    setClicked(true);
  }, [saveThumbnail]);

  useEffect(() => {
    setClicked(false);
  }, [playedSeconds]);



  return (
    <>
      {showSnapshotsList ?
        <div className={`${isMobile ? classes.mobileRoot : classes.root}`}>
          <div className={classes.helperText}>
            <Typography colorVariant="02">
              Snapshots will be saved to the parent folder
            </Typography>
          </div>

          <ul className={classes.snapshotsList}>
            <Scrollbar
              height={'100%'}
              autoHeight={isMobile ? true : false}
              autoHeightMin={200}
              width={isMobile ? '350px' : '100%'}
              colorScrollbar="main"
            >
              <div className={`${classes.listItemContainer}`} >
                {snapshots.map((item, index) => {
                  const loading = item.processing;
                  const failed = error !== undefined;
                  return (
                    <li className={classes.listItem} key={index}>
                      {loading && (
                        <div className={classes.loadingOverlay}>
                          <Loader />
                        </div>
                      )}
                      {failed && (
                        <MaybeTooltip title={'error'} disabled={false}>
                          <div className={classes.loadingOverlay}>
                            <Icon
                              icon={'error_fill'}
                              customColor={getThemeColor('color.background.critical')}
                              size={SizeVariants.md}
                            />
                          </div>
                        </MaybeTooltip>
                      )}
                      <div className={classes.imageContainer}>
                        <img
                          src={item.display_image ? item.display_image : item.thumbnail}
                          alt="snapshot"
                          onClick={() => onClickThumbnail(item.captured_at)}
                          className={classes.image}
                        />
                      </div>
                    </li>
                  );
                })}
              </div>
            </Scrollbar>
            <HBox align="center" gap={16}>
              <span
                className={`${classes.snapshotButton} ${classes.yellowBorder} ${!canCapture ? classes.disabled : ''
                  }`}
                onClick={onClickSnapshot}
              >
                <Icon
                  icon="camera"
                  size={SizeVariants.md}
                  customColor={
                    canCapture
                      ? getThemeColor('color.background.yellow.main')
                      : undefined
                  }
                />
                <Typography
                  customColor={canCapture ? '#ffd423' : ''}
                  colorVariant={canCapture ? '01' : '03'}
                >
                  Save current frame as new image
                </Typography>
              </span>

              <span
                className={`${classes.snapshotButton} ${classes.relative} ${!canSaveThumnail
                  ? classes.disabled + ' ' + classes.secondaryBorder
                  : ''
                  }`}
                onClick={canSaveThumnail ? onSaveThumbnail : null}
              >
                {saveThumbnailStatus === 'failed' && (
                  <div className={classes.loadingOverlay}>
                    <Tooltip
                      content={
                        <Typography color="critical">Reuqest failed</Typography>
                      }
                    >
                      <Icon icon="error" color="error" />
                    </Tooltip>
                  </div>
                )}
                {saveThumbnailStatus === 'loading' && (
                  <div className={classes.loadingOverlay}>
                    <Loader />
                  </div>
                )}
                <Icon
                  icon="image"
                  size={SizeVariants.md}
                  customColor={!canSaveThumnail && getColor}
                />
                <Typography colorVariant={canSaveThumnail ? '01' : '03'}>
                  Use current frame as thumbnail
                </Typography>
              </span>
            </HBox>
          </ul>
        </div> : ''}
    </>
  );
};

const getColor = (theme) => theme.color.background.secondary;
